import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import LoginPage from "./pages/login";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import AdminPage from "./pages/main";
import PrivateRoute from "./components/privateroute";
import AppLockGate from "./components/AppLockGate";
import DockScrollHints from "./components/DockScrollHints";
import { WebSocketProvider } from "./context/WebSocketContext";
import { initAndroidBackButton, pushAndroidBackHandler } from "./utils/androidBack";
import { initPushNotifications } from "./utils/pushInit";
import { initNotificationSound } from "./utils/notificationSound";
import { isCapacitorRuntime } from "./config/vars";
import "./styles/admin-modern.css";
import "./styles/admin-mobile-shell.css";

function normalizeAdminPushKind(value) {
  const raw = String(value || "").trim().toLowerCase();
  if (!raw) return "";
  if (
    [
      "license",
      "license_renewal",
      "license-renewal",
      "license_request",
      "license_renewal_request",
      "license_renewal_resolved",
    ].includes(raw)
  ) {
    return "license";
  }
  if (["news", "news_item", "announcement"].includes(raw)) return "news";
  if (["poll", "polls", "survey"].includes(raw)) return "poll";
  if (["feedback", "feedbacks"].includes(raw)) return "feedback";
  return raw;
}

function parseAdminPushDeepLink(url) {
  if (!url) return null;
  try {
    const u = new URL(String(url));
    if (u.protocol !== "wra-admin:") return null;
    if (u.hostname !== "push") return null;
    const kind = normalizeAdminPushKind(u.searchParams.get("kind"));
    if (!kind) return null;
    const out = { kind };
    const requestId = u.searchParams.get("request_id");
    const networkId = u.searchParams.get("network_id");
    const newsId = u.searchParams.get("news_id");
    const pollId = u.searchParams.get("poll_id");
    const feedbackId = u.searchParams.get("feedback_id");
    if (requestId) out.request_id = requestId;
    if (networkId) out.network_id = networkId;
    if (newsId) out.news_id = newsId;
    if (pollId) out.poll_id = pollId;
    if (feedbackId) out.feedback_id = feedbackId;
    return out;
  } catch (_) {
    return null;
  }
}

const DeepLinkHandler = () => {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!isCapacitorRuntime()) return;

    const App = window?.Capacitor?.Plugins?.App;
    if (!App?.addListener) return;

    const handleUrl = (url) => {
      const parsed = parseAdminPushDeepLink(url);
      if (!parsed) return;
      try {
        localStorage.setItem("WR_ADMIN_PENDING_PUSH_OPEN", JSON.stringify(parsed));
      } catch (_) {
        // ignore
      }
      try {
        window.dispatchEvent(new Event("wrPushOpen"));
      } catch (_) {
        // ignore
      }
      if (location?.pathname !== "/main") {
        navigate("/main");
      }
    };

    (async () => {
      try {
        const res = await App?.getLaunchUrl?.();
        if (res?.url) handleUrl(res.url);
      } catch (_) {
        // ignore
      }
    })();

    const sub = App.addListener("appUrlOpen", (event) => {
      handleUrl(event?.url);
    });

    return () => {
      try {
        sub?.remove?.();
      } catch (_) {
        // ignore
      }
    };
  }, [navigate, location?.pathname]);

  return null;
};

function AndroidBackRouterBridge() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    return pushAndroidBackHandler(() => {
      if (location.pathname === "/main") {
        return false;
      }
      if (location.pathname !== "/") {
        navigate(-1);
        return true;
      }
      return false;
    });
  }, [location.pathname, navigate]);

  return null;
}

const App = () => {
  useEffect(() => {
    initAndroidBackButton();
    initNotificationSound();
    initPushNotifications();
  }, []);

  return (
    <BrowserRouter>
      <AndroidBackRouterBridge />
      <DeepLinkHandler />
      <DockScrollHints />
      <WebSocketProvider>
        <AppLockGate>
          <Routes>
            <Route path="/" element={<LoginPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route
              path="/main"
              element={
                <PrivateRoute>
                  <AdminPage />
                </PrivateRoute>
              }
            />
          </Routes>
        </AppLockGate>
      </WebSocketProvider>
    </BrowserRouter>
  );
};

export default App;
